import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Ticket as TicketIcon } from 'lucide-react';
import EventTicket from '../components/EventTicket';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_BANNERS = {
  pending: ['Ticket not active yet', 'It activates once your team is locked and payment is complete.'],
  payment_pending: ['Payment pending', 'Your ticket is issued as soon as your payment is confirmed. Complete it from the event page.'],
  cancelled: ['This ticket was cancelled', 'This registration is no longer active. Contact the organizer if you think this is a mistake.'],
  refunded: ['This ticket was refunded', 'The payment for this ticket was refunded, so it can no longer be used for entry.'],
};

const StateBanner = ({ state, eventId }) => {
  const [title, text] = STATE_BANNERS[state];
  const warn = state === 'pending' || state === 'payment_pending';
  return (
    <div className={`text-center rounded-lg p-6 border ${warn ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid={`ticket-state-${state}`}>
      <p className={`font-semibold ${warn ? 'text-amber-800' : 'text-red-700'}`}>{title}</p>
      <p className={`text-sm mt-1 ${warn ? 'text-amber-700' : 'text-red-600'}`}>{text}</p>
      {state === 'payment_pending' && (
        <Link to={`/events/${eventId}`} className="inline-block mt-3 text-sm font-semibold text-[#002FA7]" data-testid="ticket-complete-payment-link">Go to event page</Link>
      )}
    </div>
  );
};

const EventTicketPage = () => {
  const { eventId, registrationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/events/${eventId}/ticket/${registrationId}`);
        const reg = res.data.registration;
        if (reg.event_id !== eventId || reg.id !== registrationId) {
          navigate(`/events/${reg.event_id}/ticket/${reg.id}`, { replace: true });
        }
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Ticket not found');
      } finally { setLoading(false); }
    })();
  }, [eventId, registrationId, navigate]);

  const backId = data?.registration?.event_id || eventId;
  const state = data?.registration?.ticket_state || 'active';

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="event-ticket-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to={`/events/${backId}`} className="inline-flex items-center gap-1.5 text-sm text-[#52525B] hover:text-[#0A0A0A] mb-6" data-testid="ticket-back-link">
          <ArrowLeft size={15} /> Back to event
        </Link>

        {loading ? (
          <div className="flex items-center justify-center gap-2 text-[#52525B] py-24"><Loader2 className="animate-spin" size={18} /> Loading ticket…</div>
        ) : error ? (
          <div className="text-center py-24" data-testid="ticket-error">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3"><TicketIcon className="text-red-500" size={22} /></div>
            <p className="text-[#0A0A0A] font-semibold" data-testid="ticket-error-message">{error}</p>
            <p className="text-sm text-[#A1A1AA] mt-1">Check the link in your confirmation email, or open your ticket from My Events after logging in.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-extrabold text-[#0A0A0A] tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Your Event Ticket</h1>
              <p className="text-sm text-[#A1A1AA] mt-1">Download it or share your participation</p>
            </div>
            {data.registration.team_name && (
              <p className="text-center text-sm font-semibold text-[#0A0A0A] mb-3" data-testid="ticket-team-name">Team {data.registration.team_name} · {data.registration.team_role}</p>
            )}
            {STATE_BANNERS[state] ? <StateBanner state={state} eventId={backId} /> : (
              <EventTicket event={data.event} registration={data.registration} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventTicketPage;
